import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { Check, Loader2, CreditCard, Sparkles, ArrowUpRight, ExternalLink, Zap, Download, FileText, Receipt, ShieldCheck, Users, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import MyReferralsCard from "@/components/MyReferralsCard";

const TIER_ACCENT = {
  free: "#64748B",
  pro: "#10B981",
  hustler: "#0EA5E9",
  team: "#6366F1",
};

export default function Billing() {
  const nav = useNavigate();
  const loc = useLocation();
  const [tiers, setTiers] = useState([]);
  const [status, setStatus] = useState(null);
  const [usage, setUsage] = useState(null);
  const [skipTraceQuota, setSkipTraceQuota] = useState(null);
  const [workspaceMe, setWorkspaceMe] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [cycle, setCycle] = useState("monthly"); // 'monthly' | 'annual'
  const [busyTier, setBusyTier] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  // Teams checkout — seat-quantity selectors.
  const [teamExtraMembers, setTeamExtraMembers] = useState(0);
  const [teamVAs, setTeamVAs] = useState(0);
  // Manage-seats panel (post-checkout) — reflects live quota from server.
  const [seatCosts, setSeatCosts] = useState(null);
  const [seatCostsLoading, setSeatCostsLoading] = useState(false);
  const [seatAdjustBusy, setSeatAdjustBusy] = useState(false);
  const [seatAdjustDraft, setSeatAdjustDraft] = useState({ member: 0, va: 0 });

  // Handle ?status=success / cancelled AND ?tier=X&cycle=Y&autostart=1 (deep-link from register/login).
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const s = params.get("status");
    const sid = params.get("session_id");
    if (s === "success" && sid) {
      toast.success("Subscription active — welcome to your new tier!");
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const { data } = await api.get("/billing/status");
          if (data.tier !== "free" && data.status !== "none") {
            setStatus(data);
            nav("/app/billing", { replace: true });
            return;
          }
        } catch { /* ignore */ }
        if (attempts < 4) setTimeout(poll, 2000);
        else nav("/app/billing", { replace: true });
      };
      poll();
    } else if (s === "cancelled") {
      toast.info("No charge made — you can subscribe any time.");
      track("checkout_cancelled");
      nav("/app/billing", { replace: true });
    }
  }, [loc.search, nav]);

  // Load tiers + status + usage + invoices on mount. Refresh is exposed via
  // the helper below for post-action reloads (e.g., after auto-checkout
  // succeeds). Using an inline IIFE here (instead of a useCallback) lets us
  // strip the `exhaustive-deps` suppression — the effect genuinely runs once.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [t, s, u, q, w] = await Promise.all([
          api.get("/billing/tiers"),
          api.get("/billing/status"),
          api.get("/billing/usage"),
          api.get("/skip-trace/quota").catch(() => ({ data: null })),
          api.get("/workspace/me").catch(() => ({ data: null })),
        ]);
        if (!alive) return;
        setTiers(t.data);
        setStatus(s.data);
        setUsage(u.data);
        setSkipTraceQuota(q.data);
        setWorkspaceMe(w.data);
        if (s.data?.tier && s.data.tier !== "free") {
          setInvoicesLoading(true);
          api.get("/billing/invoices", { params: { limit: 24 } })
            .then((r) => { if (alive) setInvoices(r.data?.invoices || []); })
            .catch(() => { if (alive) setInvoices([]); })
            .finally(() => { if (alive) setInvoicesLoading(false); });
        }
      } catch (err) {
        if (alive) toast.error(err?.response?.data?.detail || "Failed to load billing");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Auto-launch checkout if user arrived from a marketing CTA (?tier=pro&cycle=monthly&autostart=1).
  // Runs once after status loads, then strips the params so a refresh doesn't re-trigger.
  useEffect(() => {
    if (!status) return;
    const params = new URLSearchParams(loc.search);
    if (params.get("autostart") !== "1") return;
    const intentTier = (params.get("tier") || "").toLowerCase();
    const intentCycle = (params.get("cycle") || "monthly").toLowerCase();
    if (!["pro", "hustler", "team"].includes(intentTier)) return;
    // Block auto-launch for any tier currently marked coming_soon (e.g. Teams).
    const intentTierMeta = tiers.find((t) => t.key === intentTier);
    if (intentTierMeta?.coming_soon) {
      toast.info(`${intentTierMeta.name} isn't available yet — we'll let you know the moment it launches.`);
      nav("/app/billing", { replace: true });
      return;
    }
    // Sync the displayed cycle toggle.
    if (intentCycle === "annual" || intentCycle === "monthly") setCycle(intentCycle);
    // If they already have an active sub, don't auto-charge.
    if (status.tier !== "free" && status.status !== "none") {
      toast.info(`You're already on ${status.tier_name}. Use Manage to change plans.`);
      nav("/app/billing", { replace: true });
      return;
    }
    nav("/app/billing", { replace: true });
    // Tiny delay so the toast + UI update before redirect.
    setTimeout(() => {
      toast.success(`Starting ${intentTier} checkout…`);
      subscribe(intentTier);
    }, 400);
  }, [status]);

  const subscribe = async (tierKey) => {
    setBusyTier(tierKey);
    try {
      const body = {
        tier: tierKey,
        billing_cycle: cycle,
        origin_url: window.location.origin,
      };
      // Teams tier: include the pre-purchase seat extras from the selector.
      if (tierKey === "team") {
        body.extra_member_seats = teamExtraMembers;
        body.va_seats = teamVAs;
      }
      const { data } = await api.post("/billing/checkout", body);
      track("checkout_initiated", { tier: tierKey, cycle, metadata: { session_id: data?.session_id, extra_member_seats: body.extra_member_seats, va_seats: body.va_seats } });
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
    } finally {
      setBusyTier(null);
    }
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Portal failed");
    } finally {
      setPortalBusy(false);
    }
  };

  const currentTier = status?.tier || "free";
  const annualSavings = (monthly, annual) => Math.round(((monthly * 12 - annual) / (monthly * 12)) * 100);

  const limitOf = (tier, key) => tier?.limits?.[key];
  const fmtLimit = (n) => (n === -1 ? "∞" : String(n));

  const usageBars = useMemo(() => {
    if (!status || !usage) return [];
    const limits = status.limits || {};
    const bars = [
      { key: "max_leads", label: "Active leads", used: usage.leads, cap: limits.max_leads },
      { key: "saved_searches", label: "Saved searches", used: usage.saved_searches, cap: limits.saved_searches },
      { key: "ai_analyses_per_month", label: "AI analyses (this month)", used: usage.ai_analyses_this_month, cap: limits.ai_analyses_per_month },
      { key: "team_contacts", label: "Power Team contacts", used: usage.team_contacts, cap: limits.team_contacts },
      { key: "captures", label: "Capture forms", used: usage.captures, cap: limits.captures },
    ];
    // Only show skip-trace bar when the tier actually has any credits
    // (cap > 0). Pro + Free see nothing here — those plans don't include the feature.
    if (skipTraceQuota && skipTraceQuota.cap > 0) {
      bars.push({
        key: "skip_trace_credits_per_month",
        label: "Skip-trace credits (this month)",
        used: skipTraceQuota.used,
        cap: skipTraceQuota.cap,
      });
    }
    return bars;
  }, [status, usage, skipTraceQuota]);

  // Non-owner team seats can't manage billing — render a read-only banner instead.
  const isNonOwnerSeat = !!(workspaceMe?.workspace && !workspaceMe.is_owner);
  const isTeamOwner = currentTier === "team" && !isNonOwnerSeat;

  // Load seat costs whenever the user is the Teams owner.
  useEffect(() => {
    if (!isTeamOwner) {
      setSeatCosts(null);
      return;
    }
    let alive = true;
    setSeatCostsLoading(true);
    api.get("/workspace/seat-costs")
      .then((r) => {
        if (!alive) return;
        setSeatCosts(r.data);
        setSeatAdjustDraft({
          member: r.data?.seat_quota?.member || 0,
          va: r.data?.seat_quota?.va || 0,
        });
      })
      .catch(() => { if (alive) setSeatCosts(null); })
      .finally(() => { if (alive) setSeatCostsLoading(false); });
    return () => { alive = false; };
  }, [isTeamOwner]);

  const adjustSeats = async () => {
    setSeatAdjustBusy(true);
    try {
      const { data } = await api.post("/workspace/seats/adjust", {
        extra_member_seats: seatAdjustDraft.member,
        va_seats: seatAdjustDraft.va,
      });
      setSeatCosts(data);
      toast.success("Seat changes saved — prorated charge applied.");
      track("seats_adjusted", { member: seatAdjustDraft.member, va: seatAdjustDraft.va });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update seats.");
    } finally {
      setSeatAdjustBusy(false);
    }
  };

  const memberSeatPrice = seatCosts?.price_per_role?.member || 39;
  const vaSeatPrice = seatCosts?.price_per_role?.va || 19;

  return (
    <div data-testid="billing-page">
      <div className="topbar">
        <div>
          <div className="crumb">Billing</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">Plans &amp; Subscription</h1>
          <p className="text-[var(--ink-2)] text-sm mt-1">
            {isNonOwnerSeat
              ? "You\u2019re on a team plan. Your workspace owner manages billing \u2014 you get all the Teams-tier perks for free."
              : "Pay monthly or save ~17% annually. Pro includes a 14-day free trial — no card needed to start."}
          </p>
        </div>
        {currentTier !== "free" && !isNonOwnerSeat && (
          <button
            type="button"
            onClick={openPortal}
            disabled={portalBusy}
            className="btn btn-ghost"
            data-testid="billing-portal-btn"
          >
            {portalBusy ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />} Manage subscription
          </button>
        )}
      </div>

      {isNonOwnerSeat && (
        <div className="card p-5 mb-5 border-emerald-200 bg-emerald-50" data-testid="billing-seat-banner">
          <div className="flex items-start gap-3">
            <ShieldCheck size={20} className="text-emerald-700 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-emerald-900">You&rsquo;re a {workspaceMe.role} on {workspaceMe.workspace.name}</div>
              <p className="text-sm text-emerald-800 mt-1">
                Billing is managed by the workspace owner. You have full access to every Teams-tier feature
                (shared pipeline, AI Coach, skip-trace credits, distress filters) at no personal cost.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* current plan + usage */}
      {status && (
        <div className="card p-4 mb-5" data-testid="billing-current-plan">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs text-[var(--ink-2)] uppercase tracking-wide">Current plan</div>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className="px-2.5 py-1 rounded-full text-xs font-bold"
                  style={{ background: TIER_ACCENT[currentTier] + "22", color: TIER_ACCENT[currentTier] }}
                  data-testid="billing-current-tier-badge"
                >
                  {status.tier_name}
                </span>
                <span className="text-xs text-[var(--ink-2)]">
                  Status: <strong>{status.status}</strong>
                  {status.trial_end && ` · trial ends ${new Date(status.trial_end).toLocaleDateString()}`}
                  {status.cancel_at_period_end && status.current_period_end &&
                    ` · cancels ${new Date(status.current_period_end).toLocaleDateString()}`}
                </span>
              </div>
            </div>
          </div>
          {usageBars.length > 0 && (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-4">
              {usageBars.map((b) => {
                const unlimited = b.cap === -1;
                const pct = unlimited ? 0 : Math.min(100, Math.round((b.used / Math.max(b.cap, 1)) * 100));
                const danger = !unlimited && pct >= 80;
                return (
                  <div key={b.key} className="border border-[var(--line)] rounded-lg p-3" data-testid={`usage-${b.key}`}>
                    <div className="text-[10px] text-[var(--ink-2)] uppercase tracking-wide">{b.label}</div>
                    <div className="font-bold text-sm mt-1">
                      {b.used} <span className="text-[var(--ink-2)] font-normal">/ {fmtLimit(b.cap)}</span>
                    </div>
                    {!unlimited && (
                      <div className="h-1.5 rounded-full bg-slate-200 mt-2 overflow-hidden">
                        <div
                          className="h-full transition-all"
                          style={{ width: `${pct}%`, background: danger ? "#E76F51" : TIER_ACCENT[currentTier] }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Teams-only: manage purchased seats (post-checkout). */}
      {isTeamOwner && (
        <ManageSeatsCard
          loading={seatCostsLoading}
          costs={seatCosts}
          draft={seatAdjustDraft}
          setDraft={setSeatAdjustDraft}
          memberPrice={memberSeatPrice}
          vaPrice={vaSeatPrice}
          onSave={adjustSeats}
          busy={seatAdjustBusy}
        />
      )}

      {/* Self-service Seat-Referral attribution card — visible to all logged-in users. */}
      <MyReferralsCard />

      {/* billing cycle toggle */}
      <div className="flex items-center justify-center gap-3 mb-5">
        <button
          type="button"
          onClick={() => setCycle("monthly")}
          className={"btn " + (cycle === "monthly" ? "btn-primary" : "btn-ghost")}
          data-testid="billing-cycle-monthly"
        >Monthly</button>
        <button
          type="button"
          onClick={() => setCycle("annual")}
          className={"btn " + (cycle === "annual" ? "btn-primary" : "btn-ghost")}
          data-testid="billing-cycle-annual"
        >
          Annual <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">2 months free</span>
        </button>
      </div>

      <InvoicesSection
        invoices={invoices}
        loading={invoicesLoading}
        show={status?.tier && status.tier !== "free"}
      />

      {loading ? (
        <div className="card p-6 text-center text-[var(--ink-2)]"><Loader2 size={16} className="inline animate-spin mr-2" />Loading plans…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="billing-tier-grid">
          {tiers.map((t) => {
            const accent = TIER_ACCENT[t.key];
            const isCurrent = t.key === currentTier;
            const isBusy = busyTier === t.key;
            const isComingSoon = !!t.coming_soon;
            const price = cycle === "monthly" ? t.monthly_usd : t.annual_usd;
            const monthlyEquivalent = cycle === "annual" && t.monthly_usd ? Math.round(t.annual_usd / 12) : null;
            const savings = cycle === "annual" && t.monthly_usd ? annualSavings(t.monthly_usd, t.annual_usd) : 0;
            return (
              <div
                key={t.key}
                className={"card p-5 flex flex-col gap-3 transition relative " + (t.featured ? "ring-2" : "") + (isComingSoon ? " opacity-95" : "")}
                style={t.featured ? { ringColor: accent, borderColor: accent } : undefined}
                data-testid={`tier-card-${t.key}`}
              >
                {isComingSoon && (
                  <div
                    className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-[0.18em] bg-amber-100 text-amber-800 border border-amber-300 shadow-sm z-10"
                    data-testid={`tier-coming-soon-badge-${t.key}`}
                  >
                    Coming soon
                  </div>
                )}
                {t.featured && (
                  <div className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full self-start" style={{ background: accent + "22", color: accent }}>
                    <Sparkles size={10} className="inline mr-1" /> Most popular
                  </div>
                )}
                <div>
                  <div className="font-display font-extrabold text-xl" style={{ color: accent }}>{t.name}</div>
                  <div className="text-xs text-[var(--ink-2)] mt-0.5">{t.tagline}</div>
                </div>
                <div>
                  {t.key === "free" ? (
                    <div className="font-display font-black text-4xl">$0</div>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span className="font-display font-black text-4xl">${price.toLocaleString()}</span>
                        <span className="text-[var(--ink-2)] text-sm">{cycle === "monthly" ? "/mo" : "/yr"}</span>
                      </div>
                      {monthlyEquivalent && (
                        <div className="text-xs text-[var(--ink-2)] mt-0.5">
                          ${monthlyEquivalent}/mo billed annually · save {savings}%
                        </div>
                      )}
                      {t.trial_days > 0 && !isComingSoon && (
                        <div className="text-xs text-emerald-700 font-semibold mt-1 flex items-center gap-1">
                          <Zap size={11} /> {t.trial_days}-day free trial
                        </div>
                      )}
                    </>
                  )}
                </div>
                <ul className="flex flex-col gap-1.5 text-sm flex-1">
                  {t.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <Check size={14} className="shrink-0 mt-0.5" style={{ color: accent }} />
                      <span className="text-[var(--ink)]">{f}</span>
                    </li>
                  ))}
                </ul>
                {/* Teams-only pre-purchase seat selector. */}
                {t.key === "team" && !isComingSoon && !isCurrent && (
                  <TeamSeatSelector
                    accent={accent}
                    extraMembers={teamExtraMembers}
                    setExtraMembers={setTeamExtraMembers}
                    vas={teamVAs}
                    setVAs={setTeamVAs}
                    basePrice={price}
                    cycle={cycle}
                  />
                )}
                {isComingSoon ? (
                  <button
                    type="button"
                    className="btn btn-ghost cursor-not-allowed"
                    disabled
                    data-testid={`tier-cta-${t.key}`}
                    title="Multi-seat workspaces are in active development."
                  >
                    Coming soon
                  </button>
                ) : t.key === "free" ? (
                  <button type="button" className="btn btn-ghost" disabled data-testid={`tier-cta-${t.key}`}>
                    {isCurrent ? "Current plan" : "Free forever"}
                  </button>
                ) : isCurrent ? (
                  <button type="button" className="btn btn-ghost" onClick={openPortal} disabled={portalBusy} data-testid={`tier-cta-${t.key}`}>
                    <CreditCard size={14} /> Manage
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{ background: accent, borderColor: accent }}
                    onClick={() => subscribe(t.key)}
                    disabled={isBusy}
                    data-testid={`tier-cta-${t.key}`}
                  >
                    {isBusy ? <Loader2 size={14} className="animate-spin" /> : <ArrowUpRight size={14} />}
                    {currentTier === "free" ? `Start ${t.name}` : `Switch to ${t.name}`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="card p-4 mt-5 text-xs text-[var(--ink-2)] flex items-start gap-2" data-testid="billing-fineprint">
        <ExternalLink size={14} className="shrink-0 mt-0.5" />
        <div>
          Payments processed securely by Stripe. Cancel any time from the Customer Portal. Test mode &mdash; use card <code>4242 4242 4242 4242</code>, any future expiry, any CVC, any ZIP.
        </div>
      </div>
    </div>
  );
}

function InvoicesSection({ invoices, loading, show }) {
  if (!show) return null;
  return (
    <div className="card p-5 mb-5" data-testid="billing-invoices">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-[var(--ink)] text-white flex items-center justify-center flex-shrink-0">
            <Receipt size={18} />
          </div>
          <div className="min-w-0">
            <h3 className="font-display font-extrabold text-lg text-[var(--ink)]">Invoices & receipts</h3>
            <p className="text-xs text-[var(--ink-2)] mt-0.5">
              Past charges and downloadable PDF receipts. File the PDFs under <em>tax-deductible business software</em>.
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="py-8 text-center text-[var(--ink-2)]">
          <Loader2 size={16} className="inline animate-spin mr-2" /> Loading invoices…
        </div>
      ) : invoices.length === 0 ? (
        <div className="py-10 text-center bg-[var(--bg)] rounded-lg border border-dashed border-[var(--line)]">
          <FileText size={24} className="text-[var(--ink-3)] mx-auto mb-2" />
          <div className="text-sm text-[var(--ink-2)]">
            No invoices yet — your first charge will appear here.
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-5">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] font-semibold border-b border-[var(--line)]">
                <th className="text-left px-5 py-2.5">Invoice</th>
                <th className="text-left px-5 py-2.5">Date</th>
                <th className="text-right px-5 py-2.5">Amount</th>
                <th className="text-left px-5 py-2.5">Status</th>
                <th className="text-right px-5 py-2.5">Receipt</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <InvoiceRow key={inv.id} invoice={inv} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function InvoiceRow({ invoice }) {
  const date = invoice.created ? new Date(invoice.created * 1000) : null;
  const amount = (invoice.amount_paid || 0) / 100;
  const currency = (invoice.currency || "usd").toUpperCase();
  const statusPalette = {
    paid:           { bg: "bg-emerald-50",  fg: "text-emerald-700",  label: "Paid" },
    open:           { bg: "bg-amber-50",    fg: "text-amber-700",    label: "Open" },
    void:           { bg: "bg-slate-100",   fg: "text-slate-600",    label: "Void" },
    uncollectible:  { bg: "bg-red-50",      fg: "text-red-700",      label: "Uncollectible" },
    draft:          { bg: "bg-slate-100",   fg: "text-slate-500",    label: "Draft" },
  }[invoice.status] || { bg: "bg-slate-100", fg: "text-slate-600", label: invoice.status || "Unknown" };

  return (
    <tr className="border-b border-[var(--line)] last:border-b-0 hover:bg-[var(--bg)] transition-colors" data-testid={`invoice-row-${invoice.id}`}>
      <td className="px-5 py-3">
        <div className="font-mono text-xs text-[var(--ink)] font-semibold">
          {invoice.number || invoice.id.slice(0, 12)}
        </div>
      </td>
      <td className="px-5 py-3 text-[var(--ink-2)] text-xs whitespace-nowrap">
        {date ? date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"}
      </td>
      <td className="px-5 py-3 text-right font-display font-bold tabular-nums">
        ${amount.toFixed(2)} <span className="text-[10px] text-[var(--ink-3)] font-normal">{currency}</span>
      </td>
      <td className="px-5 py-3">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${statusPalette.bg} ${statusPalette.fg}`}>
          {statusPalette.label}
        </span>
      </td>
      <td className="px-5 py-3 text-right whitespace-nowrap">
        {invoice.invoice_pdf && (
          <a
            href={invoice.invoice_pdf}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--ink)] hover:text-[var(--primary-dark)] px-2.5 py-1 rounded-md hover:bg-white transition-colors"
            data-testid={`invoice-pdf-${invoice.id}`}
          >
            <Download size={12} /> PDF
          </a>
        )}
        {invoice.hosted_invoice_url && (
          <a
            href={invoice.hosted_invoice_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--ink)] hover:text-[var(--primary-dark)] px-2.5 py-1 rounded-md hover:bg-white transition-colors ml-1"
            data-testid={`invoice-view-${invoice.id}`}
          >
            <ExternalLink size={12} /> View
          </a>
        )}
      </td>
    </tr>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Teams seat selectors
// ──────────────────────────────────────────────────────────────────────────

const MEMBER_SEAT_MONTHLY = 39;
const MEMBER_SEAT_ANNUAL_EFFECTIVE = 35;
const VA_SEAT_MONTHLY = 19;
const VA_SEAT_ANNUAL_EFFECTIVE = 17;

function Stepper({ value, onChange, min = 0, max = 50, ariaLabel, testidPrefix }) {
  const clamp = (n) => Math.max(min, Math.min(max, n));
  return (
    <div className="inline-flex items-center gap-1 border border-[var(--line)] rounded-md p-0.5" role="group" aria-label={ariaLabel}>
      <button
        type="button"
        onClick={() => onChange(clamp(value - 1))}
        disabled={value <= min}
        className="w-7 h-7 inline-flex items-center justify-center rounded text-[var(--ink-2)] hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
        data-testid={`${testidPrefix}-decrement`}
        aria-label={`Decrease ${ariaLabel}`}
      >
        <Minus size={14} />
      </button>
      <span className="w-8 text-center font-display font-bold text-base tabular-nums" data-testid={`${testidPrefix}-value`}>
        {value}
      </span>
      <button
        type="button"
        onClick={() => onChange(clamp(value + 1))}
        disabled={value >= max}
        className="w-7 h-7 inline-flex items-center justify-center rounded text-[var(--ink-2)] hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
        data-testid={`${testidPrefix}-increment`}
        aria-label={`Increase ${ariaLabel}`}
      >
        <Plus size={14} />
      </button>
    </div>
  );
}

function TeamSeatSelector({ accent, extraMembers, setExtraMembers, vas, setVAs, basePrice, cycle }) {
  // basePrice is /yr when cycle === "annual" — convert to monthly equivalent for the running total.
  const baseMonthly = cycle === "annual" ? Math.round((basePrice || 0) / 12) : (basePrice || 0);
  // Seat add-ons inherit the base subscription's cycle so the Stripe invoice
  // stays clean. Annual cycle = ~10% off effective $/seat/mo.
  const isAnnual = cycle === "annual";
  const memberRate = isAnnual ? MEMBER_SEAT_ANNUAL_EFFECTIVE : MEMBER_SEAT_MONTHLY;
  const vaRate = isAnnual ? VA_SEAT_ANNUAL_EFFECTIVE : VA_SEAT_MONTHLY;
  const memberSavings = MEMBER_SEAT_MONTHLY - MEMBER_SEAT_ANNUAL_EFFECTIVE;
  const vaSavings = VA_SEAT_MONTHLY - VA_SEAT_ANNUAL_EFFECTIVE;
  const addonMonthly = extraMembers * memberRate + vas * vaRate;
  const totalMonthly = baseMonthly + addonMonthly;
  const monthlyEquivAddon = extraMembers * MEMBER_SEAT_MONTHLY + vas * VA_SEAT_MONTHLY;
  const annualSeatSavings = isAnnual ? (monthlyEquivAddon - addonMonthly) * 12 : 0;

  return (
    <div className="rounded-lg border border-dashed border-[var(--line)] p-3 -mx-1 my-1 bg-slate-50/50" data-testid="team-seat-selector">
      <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--ink-2)] flex items-center gap-1.5 mb-2">
        <Users size={12} /> Need more than 5 seats?
        {isAnnual && (
          <span className="ml-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[9px] font-bold uppercase tracking-wider" data-testid="annual-seat-discount-badge">
            ~10% off annual
          </span>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 py-1">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-[var(--ink)]">Extra members</div>
          <div className="text-[11px] text-[var(--ink-3)]">
            {isAnnual ? (
              <>
                <span className="line-through text-[var(--ink-3)] mr-1">${MEMBER_SEAT_MONTHLY}</span>
                <span className="text-emerald-700 font-bold">${memberRate}</span>/seat/mo · billed annually
              </>
            ) : (
              <>+${memberRate}/seat/mo</>
            )}
          </div>
        </div>
        <Stepper
          value={extraMembers}
          onChange={setExtraMembers}
          ariaLabel="extra member seats"
          testidPrefix="seat-extra-member"
        />
      </div>
      <div className="flex items-center justify-between gap-2 py-1">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-[var(--ink)]">VA seats</div>
          <div className="text-[11px] text-[var(--ink-3)]">
            {isAnnual ? (
              <>
                <span className="line-through text-[var(--ink-3)] mr-1">${VA_SEAT_MONTHLY}</span>
                <span className="text-emerald-700 font-bold">${vaRate}</span>/seat/mo · billed annually
              </>
            ) : (
              <>+${vaRate}/seat/mo · restricted role</>
            )}
          </div>
        </div>
        <Stepper
          value={vas}
          onChange={setVAs}
          ariaLabel="VA seats"
          testidPrefix="seat-va"
        />
      </div>
      {(extraMembers > 0 || vas > 0) && (
        <div className="mt-2 pt-2 border-t border-[var(--line)] space-y-0.5" data-testid="seat-selector-total">
          <div className="flex items-baseline justify-between">
            <span className="text-[11px] text-[var(--ink-2)]">Effective monthly total</span>
            <span className="font-display font-extrabold text-lg" style={{ color: accent }}>
              ${totalMonthly.toLocaleString()}<span className="text-[11px] font-normal text-[var(--ink-2)]">/mo</span>
            </span>
          </div>
          {isAnnual && annualSeatSavings > 0 && (
            <div className="flex items-baseline justify-end gap-1 text-[11px] text-emerald-700 font-semibold" data-testid="seat-selector-annual-savings">
              Save ${annualSeatSavings.toLocaleString()}/yr on seats
            </div>
          )}
          {!isAnnual && (extraMembers > 0 || vas > 0) && (
            <div className="text-[11px] text-[var(--ink-3)] text-right" data-testid="seat-selector-switch-prompt">
              Switch to annual above to save ~10% per seat.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ManageSeatsCard({ loading, costs, draft, setDraft, memberPrice, vaPrice, onSave, busy }) {
  if (loading) {
    return (
      <div className="card p-5 mb-5" data-testid="manage-seats-card">
        <div className="text-sm text-[var(--ink-2)]"><Loader2 size={14} className="inline animate-spin mr-2" />Loading seat usage…</div>
      </div>
    );
  }
  if (!costs) return null;

  const minMember = costs.headcount_overage?.member || 0;
  const minVA = costs.headcount_overage?.va || 0;
  const currentMember = costs.seat_quota?.member || 0;
  const currentVA = costs.seat_quota?.va || 0;
  const dirty = draft.member !== currentMember || draft.va !== currentVA;
  const isAnnual = costs.cycle === "annual";
  const newMonthly = (Math.max(draft.member, minMember) * memberPrice) + (Math.max(draft.va, minVA) * vaPrice);
  const currentMonthly = costs.monthly_overage_usd || 0;

  return (
    <div className="card p-5 mb-5" data-testid="manage-seats-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-indigo-600 text-white flex items-center justify-center flex-shrink-0">
            <Users size={18} />
          </div>
          <div className="min-w-0">
            <h3 className="font-display font-extrabold text-lg text-[var(--ink)] flex items-center gap-2">
              Manage team seats
              {isAnnual && (
                <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[9px] font-bold uppercase tracking-wider" data-testid="manage-seats-annual-badge">
                  Annual · ~10% off
                </span>
              )}
            </h3>
            <p className="text-xs text-[var(--ink-2)] mt-0.5">
              You have <strong>{costs.included} included</strong> seats and <strong>{costs.used}</strong> in use.
              {currentMember > 0 || currentVA > 0 ? (
                <> Currently paying for <strong>{currentMember}</strong> extra member{currentMember === 1 ? "" : "s"} and <strong>{currentVA}</strong> VA{currentVA === 1 ? "" : "s"} (+${currentMonthly}/mo).</>
              ) : (
                <> No extra seats purchased.</>
              )}
            </p>
          </div>
        </div>
      </div>
      <div className="grid gap-3">
        <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-[var(--line)]">
          <div className="min-w-0">
            <div className="text-sm font-semibold">Extra member seats</div>
            <div className="text-[11px] text-[var(--ink-3)]">
              {isAnnual ? (
                <>
                  <span className="line-through mr-1">${MEMBER_SEAT_MONTHLY}</span>
                  <span className="text-emerald-700 font-bold">${memberPrice}</span>/seat/mo · billed annually
                </>
              ) : (
                <>${memberPrice}/seat/mo</>
              )}
              {minMember > 0 && ` · minimum ${minMember} (current headcount)`}
            </div>
          </div>
          <Stepper
            value={draft.member}
            onChange={(n) => setDraft({ ...draft, member: n })}
            min={minMember}
            ariaLabel="extra member seats"
            testidPrefix="manage-member-seats"
          />
        </div>
        <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-[var(--line)]">
          <div className="min-w-0">
            <div className="text-sm font-semibold">VA seats</div>
            <div className="text-[11px] text-[var(--ink-3)]">
              {isAnnual ? (
                <>
                  <span className="line-through mr-1">${VA_SEAT_MONTHLY}</span>
                  <span className="text-emerald-700 font-bold">${vaPrice}</span>/seat/mo · billed annually
                </>
              ) : (
                <>${vaPrice}/seat/mo · restricted role</>
              )}
              {minVA > 0 && ` · minimum ${minVA} (current headcount)`}
            </div>
          </div>
          <Stepper
            value={draft.va}
            onChange={(n) => setDraft({ ...draft, va: n })}
            min={minVA}
            ariaLabel="VA seats"
            testidPrefix="manage-va-seats"
          />
        </div>
      </div>
      {dirty && (
        <div className="mt-4 flex items-center justify-between gap-3 pt-3 border-t border-[var(--line)]">
          <div className="text-sm">
            <span className="text-[var(--ink-2)]">Add-on total:&nbsp;</span>
            <span className="font-display font-bold tabular-nums">${currentMonthly}</span>
            <span className="text-[var(--ink-2)] mx-1.5">→</span>
            <span className="font-display font-extrabold tabular-nums text-indigo-700">${newMonthly}/mo</span>
            <div className="text-[11px] text-[var(--ink-3)] mt-0.5">Prorated for the rest of this billing period.</div>
          </div>
          <button
            type="button"
            onClick={onSave}
            disabled={busy}
            className="btn btn-primary"
            style={{ background: "#6366F1", borderColor: "#6366F1" }}
            data-testid="manage-seats-save"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save seat changes
          </button>
        </div>
      )}
    </div>
  );
}

